import { useRequest } from "ahooks";
import { Checkbox } from "antd";
import { useCallback, useRef, useState } from "react";
import { Else, If, Then, When } from "react-if";
import HBIcon from "../HBIcon";
import HBMessage from "../HBMessage";
import HBModal from "../HBModal";
import ModalWrap from "../HBModal/ModalWrap";
import HBConfirm from "../HBModal/confirm";
import { SortableList } from "../SortableList";
import CreateGroup from "./CreateGroup";
import EditGroup from "./EditGroup";
import styles from "./index.module.less";

import { EVENT } from "@/constants/event";
import useAppStore from "@/context/store";
import {
  createBoardFollowCollection,
  createUserFollowCollection,
  deleteBoardFollowCollection,
  deleteUserFollowCollection,
  editBoardFollowCollection,
  editBoardFollowCollectionItem,
  editUserFollowCollection,
  editUserFollowCollectionItem,
  getBoardFollowCollections,
  getUserFollowCollections,
  sortBoardFollowCollection,
  sortUserFollowCollection,
} from "@/services";

function GroupItem({
  onDelete: _onDelete,
  onEdit: _onEdit,
  onCheck: _onCheck,
  type,
  collection,
  disableSelect,
  domRef,
  handleProps,
  props,
}) {
  const [edit, setEdit] = useState(false);

  const onEdit = useCallback((evt) => {
    evt.stopPropagation();
    setEdit(true);
  }, []);

  const onCancel = useCallback(() => {
    setEdit(false);
  }, []);

  const onOk = useCallback(
    async (values) => {
      const success = _onEdit && (await _onEdit(collection, values));
      if (success) {
        setEdit(false);
      }
    },
    [_onEdit, collection]
  );

  const onDelete = useCallback(
    (evt) => {
      evt.stopPropagation();
      HBConfirm.open({
        title: "删除确认",
        content: "确定要删除分组吗？分组内画板/用户不会被删除。",
        okButtonProps: {
          "data-gd-click": "button_click",
          "data-button-name": `确认删除分组_${type}`,
        },
        async onOk() {
          _onDelete && (await _onDelete(collection));
        },
      });
    },
    [_onDelete, collection, type]
  );

  const onCheck = useCallback(() => {
    _onCheck && _onCheck(collection);
  }, [_onCheck, collection]);

  return (
    <div
      className={styles.item}
      ref={domRef}
      {...props}
      {...(edit ? null : handleProps)}
    >
      <If condition={edit}>
        <Then>
          <EditGroup
            title={collection?.title || ""}
            okButtonProps={{
              "data-gd-click": "button_click",
              "data-button-name": `确定保存分组_${type}`,
            }}
            onCancel={onCancel}
            onOk={onOk}
          />
        </Then>
        <Else>
          <div className={styles.card}>
            <div className={styles.grab}>
              <HBIcon className={styles.icon} type="drag_indicator" />
            </div>
            <div className={styles.container} onClick={onCheck}>
              <div className={styles.left}>
                <When condition={!disableSelect}>
                  <Checkbox
                    className={styles.checkbox}
                    checked={collection.checked}
                  />
                </When>
                <div className={styles.title}>{collection?.title || ""}</div>
              </div>
              <div className={styles.right}>
                <div
                  className={styles.button}
                  onClick={onEdit}
                  data-gd-click="button_click"
                  data-button-name={`编辑分组_${type}`}
                >
                  <HBIcon className={styles.icon} type="ic_edit" />
                </div>
                <div
                  className={styles.button}
                  onClick={onDelete}
                  data-gd-click="button_click"
                  data-button-name={`删除分组_${type}`}
                >
                  <HBIcon className={styles.icon} type="ic_delete" />
                </div>
              </div>
            </div>
          </div>
        </Else>
      </If>
    </div>
  );
}

function GroupManagerModal({
  type,
  disableSelect,
  collections,
  onCreate,
  onEdit,
  onDelete,
  onSort,
  onCheck,
  ...props
}) {
  return (
    <HBModal
      className={styles.modal}
      title="设置分组"
      destroyOnClose={true}
      width={480}
      {...(disableSelect ? { footer: null } : null)}
      {...props}
    >
      <div className={styles.wrapper}>
        <CreateGroup onCreate={onCreate} type={type} />
        <If condition={collections.length > 0}>
          <Then>
            <div className={styles.list}>
              <SortableList
                items={collections}
                autoScroll={true}
                getItemId={(item) => item.id}
                onSortEnd={onSort}
                renderItem={({ item, ref, props, handleProps }) => {
                  return (
                    <GroupItem
                      key={item.id}
                      type={type}
                      disableSelect={disableSelect}
                      collection={item}
                      domRef={ref}
                      handleProps={handleProps}
                      props={props}
                      onEdit={onEdit}
                      onDelete={onDelete}
                      onCheck={onCheck}
                    />
                  );
                }}
              />
            </div>
          </Then>
          <Else>
            <div className={styles.empty}>还没有创建分组</div>
          </Else>
        </If>
      </div>
    </HBModal>
  );
}
/**
 *
 * @param {{
 *    onCancel?: (param: any)=> void | Promise<any>
 *    filterCollectionId?: any
 *    maxSelectedCount?: number
 *    user?: any
 *    okButtonProps?: any
 *    onOk?: (param: any)=> void | Promise<any>
 *    okButtonProps?:any
 * }} param
 * @returns
 */
function RawUserGroupManagerModal({
  user,
  filterCollectionId,
  maxSelectedCount,
  onOk,
  onCancel,
  ...props
}) {
  const me = useAppStore((s) => s.user);
  const urlname = me?.urlname;
  const [collections, setCollections] = useState([]);
  const collectionMapRef = useRef({});

  useRequest(
    async () => {
      if (!urlname) {
        return;
      }
      const result = await getUserFollowCollections({
        urlname,
        user_id: user?.user_id || null,
      });
      if (!result.err) {
        const collections =
          result?.follow_user_collections
            ?.map((collection) => ({
              id: collection.follow_user_collection_id,
              count: collection.user_count,
              checked: collection.in_collection,
              ...collection,
            }))
            .filter((collection) => collection.id !== filterCollectionId) || [];
        setCollections(collections);
        collectionMapRef.current = collections.reduce((result, collection) => {
          result[collection.id] = {
            ...collection,
          };
          return result;
        }, {});
      }
    },
    {
      refreshDeps: [urlname],
    }
  );

  const onCreate = useCallback(async (values) => {
    if (!values?.title) {
      HBMessage.error("请输入分组名");
      return false;
    }

    const result = await createUserFollowCollection({ title: values.title });
    if (!result?.err) {
      setCollections((collections) => {
        const collection = result.follow_user_collection;
        return [
          {
            id: collection.follow_user_collection_id,
            count: collection.user_count,
            checked: false,
            ...collection,
          },
          ...collections,
        ];
      });
      HBMessage.success("创建分组成功");
    }

    return !result?.err;
  }, []);

  const onEdit = useCallback(async (collection, values) => {
    if (!values?.title) {
      HBMessage.error("请输入分组名");
      return false;
    }

    const result = await editUserFollowCollection({
      title: values.title,
      follow_user_collection_id: collection.id,
    });

    if (result?.changed) {
      setCollections((collections) => {
        return collections.map((item) => {
          if (item.id === collection.id) {
            item.title = values.title;
          }
          return item;
        });
      });
      HBMessage.success("修改分组名成功");
    }

    return !result?.err;
  }, []);

  const onDelete = useCallback(async (collection) => {
    const result = await deleteUserFollowCollection({
      follow_user_collection_id: collection.id,
    });

    if (!result.err) {
      setCollections((collections) => {
        return collections.filter((item) => {
          return collection.id !== item.id;
        });
      });
      HBMessage.success("删除分组成功");
    }
  }, []);

  const onSort = useCallback(
    async (oldIndex, newIndex) => {
      if (oldIndex === newIndex) return;
      const oldList = [...collections];
      collections.splice(newIndex, 0, collections.splice(oldIndex, 1)[0]);
      setCollections([...collections]);

      const result = await sortUserFollowCollection({
        follow_user_collection_id: oldList[oldIndex].id,
        after: oldList[oldIndex > newIndex ? newIndex - 1 : newIndex]?.id,
      });
      if (result?.err) {
        setCollections(oldList);
      }
    },
    [collections]
  );

  const onCheck = useCallback(
    (collection) => {
      setCollections((collections) => {
        const newCollections = collections.map((item) => {
          if (item.id === collection.id) {
            const newItem = {
              ...item,
              checked: !item.checked,
            };
            return newItem;
          }
          return item;
        });

        if (maxSelectedCount) {
          const count = newCollections.reduce((result, collection) => {
            if (collection.checked) {
              result++;
            }

            return result;
          }, 0);
          if (count > maxSelectedCount) {
            HBMessage.error(`每次分组最多选择${maxSelectedCount}个分组`);
            return collections;
          }
        }

        return newCollections;
      });
    },
    [maxSelectedCount]
  );

  const onDone = useCallback(async () => {
    const addIds = [];
    const removeIds = [];
    collections.forEach((collection) => {
      const oldCollection = collectionMapRef.current[collection.id];

      if (oldCollection) {
        // 判断分组是否勾选是否有变化
        if (oldCollection.checked !== collection.checked) {
          if (collection.checked) {
            addIds.push(collection.id);
          } else {
            removeIds.push(collection.id);
          }
        }
      } else {
        // 新增分组勾选上的情况
        if (collection.checked) {
          addIds.push(collection.id);
        }
      }
    });
    if (user) {
      const result = await editUserFollowCollectionItem({
        user_id: user.user_id,
        add_ids: addIds,
        remove_ids: removeIds,
      });
      if (!result?.err) {
        HBMessage.success("设置分组成功");
        window.dispatchEvent(
          new CustomEvent(EVENT.FOLLOWING_USER_GROUP_LIST_UPDATE, {
            detail: {
              user,
              collections,
            },
          })
        );
        onOk &&
          onOk({
            collections,
            addIds,
            removeIds,
          });
      }
    } else {
      onOk &&
        onOk({
          collections,
          addIds,
          removeIds,
        });
    }
  }, [collections, onOk, user]);

  const onClose = useCallback(() => {
    onCancel && onCancel();
    window.dispatchEvent(new Event(EVENT.FOLLOWING_USER_GROUP_LIST_UPDATE));
  }, [onCancel]);

  return (
    <GroupManagerModal
      title="设置用户分组"
      collections={collections}
      onCreate={onCreate}
      onEdit={onEdit}
      onDelete={onDelete}
      onSort={onSort}
      onCheck={onCheck}
      onOk={onDone}
      onCancel={onClose}
      {...props}
    />
  );
}
/**
 *
 * @param {{
 *    onCancel?: (param: any)=> void | Promise<any>
 *    board?: any
 *    filterCollectionId?: any
 *    maxSelectedCount?: number,
 *    okButtonProps?: any
 *    onOk?: (param: any)=> void | Promise<any>
 *    okButtonProps?:any
 * }} param
 * @returns
 */
function RawBoardGroupManagerModal({
  board,
  filterCollectionId,
  maxSelectedCount,
  onOk,
  onCancel,
  ...props
}) {
  const me = useAppStore((s) => s.user);
  const urlname = me?.urlname;
  const [collections, setCollections] = useState([]);
  const collectionMapRef = useRef({});

  useRequest(
    async () => {
      if (!urlname) {
        return;
      }
      const result = await getBoardFollowCollections({
        urlname,
        board_id: board?.board_id || null,
      });
      if (!result.err) {
        const collections =
          result?.follow_board_collections
            ?.map((collection) => ({
              id: collection.follow_board_collection_id,
              count: collection.board_count,
              checked: collection.in_collection,
              ...collection,
            }))
            .filter((collection) => collection.id !== filterCollectionId) || [];
        setCollections(collections);
        collectionMapRef.current = collections.reduce((result, collection) => {
          result[collection.id] = {
            ...collection,
          };
          return result;
        }, {});
      }
    },
    {
      refreshDeps: [urlname],
    }
  );

  const onCreate = useCallback(async (values) => {
    if (!values?.title) {
      HBMessage.error("请输入分组名");
      return false;
    }

    const result = await createBoardFollowCollection({ title: values.title });
    if (!result?.err) {
      setCollections((collections) => {
        const collection = result.follow_board_collection;
        return [
          {
            id: collection.follow_board_collection_id,
            count: collection.board_count,
            checked: false,
            ...collection,
          },
          ...collections,
        ];
      });
      HBMessage.success("创建分组成功");
    }

    return !result?.err;
  }, []);

  const onEdit = useCallback(async (collection, values) => {
    if (!values?.title) {
      HBMessage.error("请输入分组名");
      return false;
    }

    const result = await editBoardFollowCollection({
      title: values.title,
      follow_board_collection_id: collection.id,
    });

    if (result?.changed) {
      setCollections((collections) => {
        return collections.map((item) => {
          if (item.id === collection.id) {
            item.title = values.title;
          }
          return item;
        });
      });
      HBMessage.success("修改分组名成功");
    }

    return !result?.err;
  }, []);

  const onDelete = useCallback(async (collection) => {
    const result = await deleteBoardFollowCollection({
      follow_board_collection_id: collection.id,
    });

    if (!result.err) {
      setCollections((collections) => {
        return collections.filter((item) => {
          return collection.id !== item.id;
        });
      });
      HBMessage.success("删除分组成功");
    }
  }, []);

  const onSort = useCallback(
    async (oldIndex, newIndex) => {
      if (oldIndex === newIndex) return;
      const oldList = [...collections];
      collections.splice(newIndex, 0, collections.splice(oldIndex, 1)[0]);
      setCollections([...collections]);

      const result = await sortBoardFollowCollection({
        follow_board_collection_id: oldList[oldIndex].id,
        after: oldList[oldIndex > newIndex ? newIndex - 1 : newIndex]?.id,
      });
      if (result?.err) {
        setCollections(oldList);
      }
    },
    [collections]
  );

  const onCheck = useCallback(
    (collection) => {
      setCollections((collections) => {
        const newCollections = collections.map((item) => {
          if (item.id === collection.id) {
            const newItem = {
              ...item,
              checked: !item.checked,
            };
            return newItem;
          }
          return item;
        });

        if (maxSelectedCount) {
          const count = newCollections.reduce((result, collection) => {
            if (collection.checked) {
              result++;
            }

            return result;
          }, 0);
          if (count > maxSelectedCount) {
            HBMessage.error(`每次分组最多选择${maxSelectedCount}个分组`);
            return collections;
          }
        }

        return newCollections;
      });
    },
    [maxSelectedCount]
  );

  const onDone = useCallback(async () => {
    const addIds = [];
    const removeIds = [];
    collections.forEach((collection) => {
      const oldCollection = collectionMapRef.current[collection.id];

      if (oldCollection) {
        // 判断分组是否勾选是否有变化
        if (oldCollection.checked !== collection.checked) {
          if (collection.checked) {
            addIds.push(collection.id);
          } else {
            removeIds.push(collection.id);
          }
        }
      } else {
        // 新增分组勾选上的情况
        if (collection.checked) {
          addIds.push(collection.id);
        }
      }
    });
    if (board) {
      const result = await editBoardFollowCollectionItem({
        board_id: board.board_id,
        add_ids: addIds,
        remove_ids: removeIds,
      });
      if (!result?.err) {
        HBMessage.success("设置分组成功");
        window.dispatchEvent(
          new CustomEvent(EVENT.FOLLOWING_BOARD_GROUP_LIST_UPDATE, {
            detail: {
              board,
              collections,
            },
          })
        );
        onOk &&
          onOk({
            collections,
            addIds,
            removeIds,
          });
      }
    } else {
      onOk &&
        onOk({
          collections,
          addIds,
          removeIds,
        });
    }
  }, [collections, onOk, board]);

  const onClose = useCallback(() => {
    onCancel && onCancel();
    window.dispatchEvent(new Event(EVENT.FOLLOWING_BOARD_GROUP_LIST_UPDATE));
  }, [onCancel]);

  return (
    <GroupManagerModal
      title="设置画板分组"
      collections={collections}
      onCreate={onCreate}
      onEdit={onEdit}
      onDelete={onDelete}
      onSort={onSort}
      onCheck={onCheck}
      onOk={onDone}
      onCancel={onClose}
      {...props}
    />
  );
}

export const BoardGroupManagerModal = ModalWrap(RawBoardGroupManagerModal);

export const UserGroupManagerModal = ModalWrap(RawUserGroupManagerModal);

export default GroupManagerModal;
